import React, { useState, useMemo } from 'react';
import { Box, Typography, Paper, Chip, IconButton, Tooltip, MenuItem } from '@mui/material';
import { Select, TextField, InputAdornment, TableContainer } from '@mui/material';
import { Table, TableHead, TableRow, TableCell, TableSortLabel, TableBody } from '@mui/material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs } from '@mui/material';
import { Tab } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterListIcon from '@mui/icons-material/FilterList';
import TimerIcon from '@mui/icons-material/Timer';
import BlockIcon from '@mui/icons-material/Block';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ethers } from 'ethers';
import logger from '../../utils/logger';

interface Transaction {
    hash: string;
    from: string;
    to: string;
    value: string;
    timestamp: number;
    status: 'pending' | 'success' | 'error';
    gasUsed?: string;
    gasCost?: string;
    data?: string;
    error?: string;
    description?: string;
    internalTransactions?: any[];
    decodedData?: {
        contract: string;
        method: string;
        args: any;
        signature: string;
    } | null;
    relatedOpHash?: string; // Add this line
    relatedContracts?: string[];
    isTimelockRelated?: boolean;
}

interface EnhancedTransactionHistoryProps {
    transactions: Transaction[];
    contracts: { name: string; address: string; abi: any }[];
}

const BASE_EXPLORER_URL = 'https://basescan.org/tx/';

// Helper to truncate addresses and data
const truncateAddress = (address: string) =>
    `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;

const truncateData = (data: string, showFull: boolean = false) => {
    if (!data) return 'N/A';
    if (showFull) return data;
    return `${data.substring(0, 10)}...${data.substring(data.length - 8)}`;
};

// Helper to format timestamp
const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
};

// Helper to copy to clipboard
const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
        .then(() => {
            alert('Copied to clipboard');
        })
        .catch(err => {
            logger.error('Failed to copy: ', err);
        });
};

const EnhancedTransactionHistory: React.FC<EnhancedTransactionHistoryProps> = ({
    transactions,
    contracts
}) => {
    const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [showFullData, setShowFullData] = useState<Record<string, boolean>>({});
    const [sortConfig, setSortConfig] = useState<{ key: keyof Transaction; direction: 'asc' | 'desc' }>({
        key: 'timestamp',
        direction: 'desc'
    });
    const [filter, setFilter] = useState({
        status: 'all',
        contract: 'all',
        minValue: ''
    });

    const sortedTransactions = useMemo(() => {
        let filteredTxs = transactions.filter(tx => {
            const statusMatch = filter.status === 'all' || tx.status === filter.status;
            const contractMatch = filter.contract === 'all' ||
                (tx.relatedContracts || []).includes(filter.contract);
            const valueMatch = filter.minValue
                ? parseFloat(ethers.utils.formatEther(tx.value)) >= parseFloat(filter.minValue)
                : true;

            return statusMatch && contractMatch && valueMatch;
        });

        return [...filteredTxs].sort((a, b) => {
            if (a[sortConfig.key] != null && b[sortConfig.key] != null && a[sortConfig.key] !== undefined && b[sortConfig.key] !== undefined && a[sortConfig.key]! < b[sortConfig.key]!) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if ((a[sortConfig.key] ?? 0) > (b[sortConfig.key] ?? 0)) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [transactions, sortConfig, filter]);

    // Function to toggle data visibility
    const toggleDataVisibility = (hash: string) => {
        setShowFullData(prev => ({
            ...prev,
            [hash]: !prev[hash]
        }));
    };

    // Open detailed view dialog
    const openDetailedView = (tx: Transaction) => {
        setSelectedTx(tx);
        setDialogOpen(true);
    };

    const handleSort = (key: keyof Transaction) => {
        const isAsc = sortConfig.key === key && sortConfig.direction === 'asc';
        setSortConfig({ key, direction: isAsc ? 'desc' : 'asc' });
    };

    const getStatusIcon = (status: 'pending' | 'success' | 'error') => {
        switch (status) {
            case 'success': return <CheckCircleIcon color="success" />;
            case 'error': return <ErrorIcon color="error" />;
            case 'pending': return <AccessTimeIcon color="warning" />;
        }
    };

    const renderContractTags = (relatedContracts?: string[]) => {
        if (!relatedContracts) return null;
        return relatedContracts.map(contract => (
            <Chip
                key={contract}
                label={contract}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
            />
        ));
    };

    const renderFilterSection = () => (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                startAdornment={
                    <InputAdornment position="start">
                        <FilterListIcon />
                    </InputAdornment>
                }
            >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="error">Error</MenuItem>
            </Select>

            <Select
                value={filter.contract}
                onChange={(e) => setFilter(prev => ({ ...prev, contract: e.target.value }))}
            >
                <MenuItem value="all">All Contracts</MenuItem>
                {contracts.map(contract => (
                    <MenuItem key={contract.name} value={contract.name}>
                        {contract.name}
                    </MenuItem>
                ))}
            </Select>

            <TextField
                label="Min Value (ETH)"
                type="number"
                value={filter.minValue}
                onChange={(e) => setFilter(prev => ({ ...prev, minValue: e.target.value }))}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <TimerIcon />
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );

    const renderTransactionTable = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig.key === 'timestamp'}
                                direction={sortConfig.direction}
                                onClick={() => handleSort('timestamp')}
                            >
                                Timestamp
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig.key === 'value'}
                                direction={sortConfig.direction}
                                onClick={() => handleSort('value')}
                            >
                                Value (ETH)
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Contracts</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedTransactions.map((tx: Transaction) => (
                        <TableRow key={tx.hash}>
                            <TableCell>
                                {formatTime(tx.timestamp)}
                            </TableCell>
                            <TableCell>{getStatusIcon(tx.status)}</TableCell>
                            <TableCell>
                                {truncateAddress(tx.from)}
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(tx.from)}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                            <TableCell>
                                {truncateAddress(tx.to)}
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(tx.to)}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                            <TableCell>
                                {ethers.utils.formatEther(tx.value)}
                                {tx.isTimelockRelated && (
                                    <Tooltip title="Timelock Related Transaction">
                                        <BlockIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell>
                                {renderContractTags(tx.relatedContracts)}
                            </TableCell>
                            <TableCell>
                                <Tooltip title="View on Block Explorer">
                                    <IconButton
                                        href={`${BASE_EXPLORER_URL}${tx.hash}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <LinkIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="View Details">
                                    <IconButton
                                        size="small"
                                        onClick={() => openDetailedView(tx)}
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                    {/* Dialog for Detailed Transaction View */}
                    <Dialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>Transaction Details</DialogTitle>
                        <DialogContent>
                            {selectedTx && (
                                <Box>
                                    <Typography variant="h6">Transaction Hash: {selectedTx.hash}</Typography>
                                    {/* Add more detailed transaction information */}
                                    <Typography>From: {selectedTx.from}</Typography>
                                    <Typography>To: {selectedTx.to}</Typography>
                                    <Typography>Value: {selectedTx.value}</Typography>
                                    {/* Add more details as needed */}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDialogOpen(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (transactions.length === 0) {
        return (
            <Paper sx={{ p: 2, mt: 4 }}>
                <Typography variant="subtitle1">No transactions recorded yet</Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Transaction History
            </Typography>

            {renderFilterSection()}
            {renderTransactionTable()}

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Transaction Chain Details</DialogTitle>
                <DialogContent dividers>
                    {selectedTx && (
                        <>
                            <Tabs
                                value={activeTab}
                                onChange={(_, newValue) => setActiveTab(newValue)}
                                sx={{ mb: 2 }}
                            >
                                <Tab label="Main Transaction" />
                                <Tab
                                    label={`Internal Events (${selectedTx.internalTransactions?.length || 0})`}
                                    disabled={!selectedTx.internalTransactions?.length}
                                />
                                <Tab label="Raw Data" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Typography variant="h6">
                                        {selectedTx.description || 'Transaction Details'}
                                    </Typography>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 2 }}>
                                        <Typography variant="body2" fontWeight="bold">Transaction Hash:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{selectedTx.hash}</Typography>

                                        <Typography variant="body2" fontWeight="bold">From:</Typography>
                                        <Typography variant="body2">{selectedTx.from}</Typography>

                                        <Typography variant="body2" fontWeight="bold">To:</Typography>
                                        <Typography variant="body2">{selectedTx.to}</Typography>

                                        <Typography variant="body2" fontWeight="bold">Value:</Typography>
                                        <Typography variant="body2">{ethers.utils.formatEther(selectedTx.value)} ETH</Typography>

                                        <Typography variant="body2" fontWeight="bold">Status:</Typography>
                                        <Typography variant="body2" component="div">
                                            <Chip
                                                label={selectedTx.status}
                                                color={selectedTx.status === 'success' ? 'success' : selectedTx.status === 'error' ? 'error' : 'warning'}
                                                size="small"
                                            />
                                        </Typography>

                                        <Typography variant="body2" fontWeight="bold">Timestamp:</Typography>
                                        <Typography variant="body2">{new Date(selectedTx.timestamp).toLocaleString()}</Typography>

                                        {selectedTx.data && (
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">Transaction Data:</Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        wordBreak: 'break-all',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => copyToClipboard(selectedTx.data || '')}
                                                >
                                                    {selectedTx.data}
                                                </Typography>
                                            </Box>
                                        )}

                                        {selectedTx.gasUsed && (
                                            <>
                                                <Typography variant="body2" fontWeight="bold">Gas Used:</Typography>
                                                <Typography variant="body2">{selectedTx.gasUsed}</Typography>
                                            </>
                                        )}

                                        {selectedTx.gasCost && (
                                            <>
                                                <Typography variant="body2" fontWeight="bold">Gas Cost:</Typography>
                                                <Typography variant="body2">{selectedTx.gasCost} ETH</Typography>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {activeTab === 1 && selectedTx.internalTransactions && (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                        Internal Events Triggered by This Transaction
                                    </Typography>

                                    {selectedTx.internalTransactions.map((event, i) => (
                                        <Accordion key={`internal-${i}`} sx={{ mb: 1 }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography>
                                                    {event.description || `Event from ${event.contract || 'Unknown Contract'}`}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1 }}>
                                                    {event.eventName && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">Event Name:</Typography>
                                                            <Typography variant="body2">{event.eventName}</Typography>
                                                        </>
                                                    )}

                                                    {event.contract && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">Contract:</Typography>
                                                            <Typography variant="body2">{event.contract}</Typography>
                                                        </>
                                                    )}

                                                    {event.contractAddress && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">Contract Address:</Typography>
                                                            <Typography variant="body2">{event.contractAddress}</Typography>
                                                        </>
                                                    )}

                                                    {event.from && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">From:</Typography>
                                                            <Typography variant="body2">{event.from}</Typography>
                                                        </>
                                                    )}

                                                    {event.to && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">To:</Typography>
                                                            <Typography variant="body2">{event.to}</Typography>
                                                        </>
                                                    )}

                                                    {event.value && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">Value:</Typography>
                                                            <Typography variant="body2">
                                                                {ethers.BigNumber.isBigNumber(event.value)
                                                                    ? ethers.utils.formatEther(event.value)
                                                                    : event.value
                                                                }
                                                            </Typography>
                                                        </>
                                                    )}

                                                    {event.args && (
                                                        <>
                                                            <Typography variant="body2" fontWeight="bold">Arguments:</Typography>
                                                            <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto' }}>
                                                                <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                                                    {JSON.stringify(event.args, (key, value) => {
                                                                        if (typeof value === 'object' && value !== null && value.type === 'BigNumber') {
                                                                            return ethers.BigNumber.from(value).toString();
                                                                        }
                                                                        return value;
                                                                    }, 2)}
                                                                </pre>
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                            )}

                            {activeTab === 2 && (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                        Raw Transaction Data
                                    </Typography>

                                    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflowX: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                            {JSON.stringify(selectedTx, null, 2)}
                                        </pre>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnhancedTransactionHistory;
